import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.element.modalInstance = this
    this.backdrop = null
    this.lastFocused = null
    this._onKeydown = this._onKeydown.bind(this)
    this._onGlobalClick = this._onGlobalClick.bind(this)
    this._onBackdropClick = this._onBackdropClick.bind(this)

    document.addEventListener("click", this._onGlobalClick)
    this.element.addEventListener("click", this._onBackdropClick)
  }

  disconnect() {
    document.removeEventListener("click", this._onGlobalClick)
    document.removeEventListener("keydown", this._onKeydown)
    this.element.removeEventListener("click", this._onBackdropClick)
    if (this.backdrop) {
      this.backdrop.remove()
    }
  }

  _onGlobalClick(event) {
    const trigger = event.target.closest('[data-bs-toggle="modal"]') || event.target.closest('[data-modal-toggle]')
    if (trigger) {
      const targetId = trigger.getAttribute("data-bs-target") || trigger.getAttribute("data-modal-target") || trigger.getAttribute("href")
      if (targetId && document.querySelector(targetId) === this.element) {
        event.preventDefault()
        this.show(trigger)
      }
      return
    }

    const dismiss = event.target.closest('[data-bs-dismiss="modal"]') || event.target.closest('[data-modal-dismiss]')
    if (dismiss && this.element.contains(dismiss)) {
      event.preventDefault()
      this.hide()
    }
  }

  show(relatedTarget = null) {
    if (this.element.classList.contains("show")) return

    const showEvent = this._dispatch("show.bs.modal", relatedTarget)
    this._dispatch("modal:show", relatedTarget)
    if (showEvent.defaultPrevented) return

    this.isStatic = this.element.getAttribute("data-bs-backdrop") === "static" || this.element.getAttribute("data-modal-backdrop") === "static"
    this.keyboard = this.element.getAttribute("data-bs-keyboard") !== "false" && this.element.getAttribute("data-modal-keyboard") !== "false"
    this.lastFocused = document.activeElement

    this.backdrop = document.createElement("div")
    this.backdrop.className = "modal-backdrop fade"
    document.body.appendChild(this.backdrop)

    document.body.classList.add("modal-open")
    this.element.style.display = "block"
    
    this.element.offsetHeight 
    
    this.element.classList.add("show")
    this.backdrop.classList.add("show")
    this.element.removeAttribute("aria-hidden")
    this.element.setAttribute("aria-modal", "true")
    this.element.setAttribute("role", "dialog")

    const focusable = this._focusable()
    const focusTarget = focusable[0] || this.element
    focusTarget.focus({ preventScroll: true })

    document.addEventListener("keydown", this._onKeydown)

    this._dispatch("shown.bs.modal", relatedTarget)
    this._dispatch("modal:shown", relatedTarget)
  }

  hide() {
    if (!this.element.classList.contains("show")) return

    const hideEvent = this._dispatch("hide.bs.modal")
    this._dispatch("modal:hide")
    if (hideEvent.defaultPrevented) return

    if (this.element.contains(document.activeElement)) {
      if (this.lastFocused && typeof this.lastFocused.focus === "function") {
        this.lastFocused.focus({ preventScroll: true })
      } else if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur()
      }
    }

    this.element.classList.remove("show")
    if (this.backdrop) {
      this.backdrop.classList.remove("show")
    }
    this.element.setAttribute("aria-hidden", "true")
    this.element.removeAttribute("aria-modal")

    const cleanup = () => {
      this.element.style.display = "none"
      if (this.backdrop) {
        this.backdrop.remove()
        this.backdrop = null
      }
      
      if (!document.querySelector(".modal.show")) {
        document.body.classList.remove("modal-open")
      }
      
      document.removeEventListener("keydown", this._onKeydown)
      if (this.lastFocused && typeof this.lastFocused.focus === "function") {
        this.lastFocused.focus({ preventScroll: true })
      }

      this._dispatch("hidden.bs.modal")
      this._dispatch("modal:hidden")
    }

    window.setTimeout(cleanup, 300)
  }

  toggle(relatedTarget = null) {
    this.element.classList.contains("show") ? this.hide() : this.show(relatedTarget)
  }

  _onBackdropClick(event) {
    if (!this.element.classList.contains("show") || this.isStatic) return

    const modalContent = this.element.querySelector(".modal-content")
    if (modalContent && modalContent.contains(event.target)) {
      return
    }

    this.hide()
  }

  _onKeydown(event) {
    if (event.key === "Escape") {
      if (this.keyboard) {
        this.hide()
      }
      return
    }
    if (event.key === "Tab") {
      const items = this._focusable()
      if (!items.length) return
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault()
        lastEl.focus()
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault()
        firstEl.focus()
      }
    }
  }

  _focusable() {
    return Array.from(
      this.element.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null)
  }

  _dispatch(type, relatedTarget = null) {
    const event = new CustomEvent(type, { bubbles: true, cancelable: true })
    if (relatedTarget) {
      event.relatedTarget = relatedTarget
    }
    this.element.dispatchEvent(event)
    return event
  }
}
